'use server'

/**
 * Server Actions for Asset Library Management
 *
 * 모델 라이브러리, 애니메이션 라이브러리, 시나리오 설정을 위한 Server Actions
 */

import { auth } from '@clerk/nextjs/server'
import { revalidatePath, revalidateTag } from 'next/cache'
import type {
  AnimationLibraryItem,
  AnimationLibraryCreate,
  ModelLibraryItem,
  ModelLibraryCreate,
  ScenarioConfiguration,
  ScenarioConfigurationCreate,
  ScenarioConfigurationUpdate,
  ScenarioConfigurationBulkSave,
  DefaultModelInfo,
  UploadUrlResponse,
  BodyType,
} from '@/features/asset-library/types'

const NEXT_PUBLIC_BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://backend:8500'

// ============ Helper Functions ============

async function getAuthHeaders() {
  const { userId } = await auth()

  if (!userId) {
    throw new Error('Unauthorized')
  }

  return {
    'Authorization': `Bearer ${userId}`,
    'X-User-Id': userId,
    'Content-Type': 'application/json',
  }
}

function revalidateAssetLibrary() {
  revalidateTag('asset-library', 'max')
  revalidateTag('animation-library', 'max')
  revalidateTag('model-library', 'max')
  revalidateTag('scenario-configurations', 'max')
}

// ============ Animation Library ============

/**
 * 애니메이션 라이브러리 목록 조회
 */
export async function getAnimations(bodyType?: BodyType): Promise<AnimationLibraryItem[]> {
  try {
    const headers = await getAuthHeaders()
    const url = new URL(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/animations`)

    if (bodyType) {
      url.searchParams.set('body_type', bodyType)
    }

    const response = await fetch(url.toString(), {
      headers,
      cache: 'no-store',
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch animations: ${response.status}`)
    }

    const data = await response.json()
    return data.map(transformAnimationResponse)
  } catch (error) {
    console.error('[Server Action] getAnimations error:', error)
    throw error
  }
}

/**
 * 공개 애니메이션 라이브러리 조회 (인증 불필요)
 * 슬러그 페이지에서 사용
 */
export async function getAnimationsPublic(bodyType?: BodyType, agentId?: string): Promise<AnimationLibraryItem[]> {
  try {
    const url = new URL(`${NEXT_PUBLIC_BACKEND_URL}/api/public/asset-library/animations`)

    if (!agentId) {
      console.warn('[Server Action] getAnimationsPublic: agentId is required for public route')
      return []
    }

    url.searchParams.set('agent_id', agentId)

    if (bodyType) {
      url.searchParams.set('body_type', bodyType)
    }

    const response = await fetch(url.toString(), {
      cache: 'no-store',
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch public animations: ${response.status}`)
    }

    const data = await response.json()
    return data.map(transformAnimationResponse)
  } catch (error) {
    console.error('[Server Action] getAnimationsPublic error:', error)
    return []
  }
}

/**
 * 애니메이션 단건 조회
 */
export async function getAnimation(animationId: string): Promise<AnimationLibraryItem | null> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/animations/${animationId}`, {
      headers,
      cache: 'no-store',
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Failed to fetch animation: ${response.status}`)
    }

    const data = await response.json()
    return transformAnimationResponse(data)
  } catch (error) {
    console.error('[Server Action] getAnimation error:', error)
    throw error
  }
}

/**
 * 애니메이션 라이브러리에 추가
 */
export async function createAnimation(
  data: AnimationLibraryCreate,
  animationStoragePath: string
): Promise<AnimationLibraryItem> {
  try {
    const headers = await getAuthHeaders()

    const url = new URL(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/animations`)
    url.searchParams.set('name', data.name)
    url.searchParams.set('body_type', data.bodyType)
    url.searchParams.set('animation_storage_path', animationStoragePath)

    if (data.tags && data.tags.length > 0) {
      url.searchParams.set('tags', JSON.stringify(data.tags))
    }

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to create animation: ${response.status}`)
    }

    const result = await response.json()
    revalidateAssetLibrary()
    return transformAnimationResponse(result)
  } catch (error) {
    console.error('[Server Action] createAnimation error:', error)
    throw error
  }
}

/**
 * 애니메이션 삭제
 */
export async function deleteAnimation(animationId: string): Promise<boolean> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/animations/${animationId}`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to delete animation: ${response.status}`)
    }

    revalidateAssetLibrary()
    return true
  } catch (error) {
    console.error('[Server Action] deleteAnimation error:', error)
    throw error
  }
}

// ============ Model Library ============

/**
 * 모델 라이브러리 목록 조회
 */
export async function getModels(bodyType?: BodyType): Promise<ModelLibraryItem[]> {
  try {
    const headers = await getAuthHeaders()
    const url = new URL(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/models`)

    if (bodyType) {
      url.searchParams.set('body_type', bodyType)
    }

    const response = await fetch(url.toString(), {
      headers,
      cache: 'no-store',
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch models: ${response.status}`)
    }

    const data = await response.json()
    return data.map(transformModelResponse)
  } catch (error) {
    console.error('[Server Action] getModels error:', error)
    throw error
  }
}

/**
 * 모델 단건 조회
 */
export async function getModel(modelId: string): Promise<ModelLibraryItem | null> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/models/${modelId}`, {
      headers,
      cache: 'no-store',
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Failed to fetch model: ${response.status}`)
    }

    const data = await response.json()
    return transformModelResponse(data)
  } catch (error) {
    console.error('[Server Action] getModel error:', error)
    throw error
  }
}

/**
 * 모델 라이브러리에 추가
 */
export async function createModel(
  data: ModelLibraryCreate,
  modelStoragePath: string
): Promise<ModelLibraryItem> {
  try {
    const headers = await getAuthHeaders()

    const url = new URL(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/models`)
    url.searchParams.set('name', data.name)
    url.searchParams.set('body_type', data.bodyType)
    url.searchParams.set('model_storage_path', modelStoragePath)

    if (data.description) {
      url.searchParams.set('description', data.description)
    }

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to create model: ${response.status}`)
    }

    const result = await response.json()
    revalidateAssetLibrary()
    return transformModelResponse(result)
  } catch (error) {
    console.error('[Server Action] createModel error:', error)
    throw error
  }
}

/**
 * 모델 삭제
 */
export async function deleteModel(modelId: string): Promise<boolean> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/models/${modelId}`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to delete model: ${response.status}`)
    }

    revalidateAssetLibrary()
    return true
  } catch (error) {
    console.error('[Server Action] deleteModel error:', error)
    throw error
  }
}

// ============ Scenario Configuration ============

/**
 * 아바타의 시나리오 설정 목록 조회
 */
export async function getScenarioConfigs(customAvatarId: string): Promise<ScenarioConfiguration[]> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/scenarios/${customAvatarId}`, {
      headers,
      cache: 'no-store',
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch scenario configs: ${response.status}`)
    }

    const data = await response.json()
    return data.map(transformScenarioConfigResponse)
  } catch (error) {
    console.error('[Server Action] getScenarioConfigs error:', error)
    throw error
  }
}

/**
 * 공개 시나리오 설정 조회 (인증 불필요)
 * 슬러그 페이지에서 사용
 */
export async function getScenarioConfigsPublic(customAvatarId: string): Promise<ScenarioConfiguration[]> {
  try {
    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/public/asset-library/scenarios/${customAvatarId}`, {
      cache: 'no-store',
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch public scenario configs: ${response.status}`)
    }

    const data = await response.json()
    return data.map(transformScenarioConfigResponse)
  } catch (error) {
    console.error('[Server Action] getScenarioConfigsPublic error:', error)
    return []
  }
}

/**
 * 시나리오 설정 수정
 */
export async function updateScenarioConfig(
  scenarioId: string,
  data: ScenarioConfigurationUpdate
): Promise<ScenarioConfiguration | null> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/scenarios/${scenarioId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        animations: data.animations,
        play_mode: data.playMode,
        blend_duration: data.blendDuration,
      }),
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Failed to update scenario config: ${response.status}`)
    }

    const result = await response.json()
    revalidateAssetLibrary()
    return transformScenarioConfigResponse(result)
  } catch (error) {
    console.error('[Server Action] updateScenarioConfig error:', error)
    throw error
  }
}

/**
 * 시나리오 설정 일괄 저장
 */
export async function saveScenarioConfigs(
  customAvatarId: string,
  configurations: ScenarioConfigurationCreate[]
): Promise<ScenarioConfiguration[]> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/scenarios/bulk`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        custom_avatar_id: customAvatarId,
        configurations: configurations.map(config => ({
          scenario_type: config.scenarioType,
          animations: config.animations.map(anim => ({
            animation_id: anim.animationId,
            is_default: anim.isDefault,
            order: anim.order,
          })),
          play_mode: config.playMode,
          blend_duration: config.blendDuration,
        })),
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to save scenario configs: ${response.status}`)
    }

    const result = await response.json()
    revalidateAssetLibrary()
    return result.map(transformScenarioConfigResponse)
  } catch (error) {
    console.error('[Server Action] saveScenarioConfigs error:', error)
    throw error
  }
}

/**
 * 시나리오 설정 삭제
 */
export async function deleteScenarioConfig(scenarioId: string): Promise<boolean> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/scenarios/${scenarioId}`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to delete scenario config: ${response.status}`)
    }

    revalidateAssetLibrary()
    return true
  } catch (error) {
    console.error('[Server Action] deleteScenarioConfig error:', error)
    throw error
  }
}

// ============ Utility Functions ============

/**
 * 등신별 기본 모델 정보 조회
 */
export async function getDefaultModels(): Promise<DefaultModelInfo[]> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/default-models`, {
      headers,
      cache: 'force-cache',
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch default models: ${response.status}`)
    }

    const data = await response.json()
    return data.models.map((model: { body_type: string; model_name: string; model_url: string }) => ({
      bodyType: model.body_type as BodyType,
      modelName: model.model_name,
      modelUrl: model.model_url,
    }))
  } catch (error) {
    console.error('[Server Action] getDefaultModels error:', error)
    throw error
  }
}

/**
 * Presigned URL 발급
 */
export async function getUploadUrl(
  fileType: 'model' | 'animation' | 'thumbnail',
  filename: string
): Promise<UploadUrlResponse> {
  try {
    const headers = await getAuthHeaders()

    const url = new URL(`${NEXT_PUBLIC_BACKEND_URL}/api/asset-library/upload-url`)
    url.searchParams.set('file_type', fileType)
    url.searchParams.set('filename', filename)

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to get upload URL: ${response.status}`)
    }

    const data = await response.json()
    return {
      uploadUrl: data.upload_url,
      storagePath: data.storage_path,
      publicUrl: data.public_url,
    }
  } catch (error) {
    console.error('[Server Action] getUploadUrl error:', error)
    throw error
  }
}

// ============ Response Transformers ============

function transformAnimationResponse(data: Record<string, unknown>): AnimationLibraryItem {
  return {
    id: data.id as string,
    userId: data.user_id as string,
    name: data.name as string,
    bodyType: data.body_type as BodyType,
    animationUrl: data.animation_url as string,
    animationStoragePath: data.animation_storage_path as string,
    thumbnailUrl: data.thumbnail_url as string | undefined,
    tags: (data.tags as string[]) || [],
    isActive: data.is_active as boolean,
    createdAt: data.created_at as string,
    updatedAt: data.updated_at as string,
  }
}

function transformModelResponse(data: Record<string, unknown>): ModelLibraryItem {
  return {
    id: data.id as string,
    userId: data.user_id as string,
    name: data.name as string,
    bodyType: data.body_type as BodyType,
    modelUrl: data.model_url as string,
    modelStoragePath: data.model_storage_path as string,
    thumbnailUrl: data.thumbnail_url as string | undefined,
    description: data.description as string | undefined,
    isActive: data.is_active as boolean,
    createdAt: data.created_at as string,
    updatedAt: data.updated_at as string,
  }
}

function transformScenarioConfigResponse(data: Record<string, unknown>): ScenarioConfiguration {
  return {
    id: data.id as string,
    userId: data.user_id as string,
    customAvatarId: data.custom_avatar_id as string,
    scenarioType: data.scenario_type as string,
    animations: (data.animations as Array<{
      animation_id: string
      is_default: boolean
      order: number
    }>).map(a => ({
      animationId: a.animation_id,
      isDefault: a.is_default,
      order: a.order,
    })),
    playMode: data.play_mode as ScenarioConfiguration['playMode'],
    blendDuration: data.blend_duration as number,
    createdAt: data.created_at as string,
    updatedAt: data.updated_at as string,
  }
}
